import React, { useEffect, useState, useCallback } from "react";
import {
  useDeleteSalaryMutation,
  useLazyGetEmployeeQuery,
  useModifySalaryMutation,
  usePostSalaryMutation,
} from "../../service/salaryService";
import EmployeeSalaryTable from "./SalaryTable";
import { Button, Form, message, Modal } from "antd";
import SalaryForm from "./SalaryForm";
import { payloadFormatter } from "./index.config";
import DeleteModal from "../../components/common/DeleteModal";

const Salary = () => {
  const [modalState, setModalState] = useState({
    open: false,
    current: null,
    canEdit: false,
    isDeleteModalVisible: false,
  });
  const [date, setDate] = useState(new Date());

  const [form] = Form.useForm();

  const [getData, { data, isError, isFetching: isLoading }] =
    useLazyGetEmployeeQuery();
  const [postSalary, { isLoading: postLoad }] = usePostSalaryMutation();
  const [deleteSalary, { isLoading: deleteLoad }] = useDeleteSalaryMutation();
  const [modifySalary] = useModifySalaryMutation();

  const fetchData = useCallback(() => {
    const currentDate = new Date(date);
    getData({ month: currentDate });
  }, [date, getData]);

  useEffect(() => {
    fetchData();
  }, [date, fetchData]);

  const onFinish = async () => {
    try {
      const values = await form.validateFields();
      const salary = modalState.canEdit ? modifySalary : postSalary;
      const payload = payloadFormatter(values, modalState.current);
      const pay = modalState.canEdit
        ? { payload, id: modalState.current?.paymentDetails?._id }
        : { payload };
      const response = await salary(pay).unwrap();
      message.success(response?.message);
      resetForm();
    } catch (err) {
      message.error(
        err?.data?.message ||
          "Please fill in all required fields before proceeding."
      );
    }
  };

  const resetForm = () => {
    setModalState((prevState) => ({
      ...prevState,
      current: null,
      open: false,
    }));
    form.resetFields();
  };

  const handleDelete = async () => {
    try {
      const response = await deleteSalary(
        modalState.current?.paymentDetails?._id
      ).unwrap();
      message.success(response?.message);
      setModalState((prevState) => ({
        ...prevState,
        isDeleteModalVisible: false,
        current: null,
      }));
    } catch (err) {
      message.error(err?.data?.message);
    }
  };

  const footerContent = (
    <Button
      type="primary"
      className="text-white bg-blue-600"
      onClick={onFinish}
      loading={postLoad}
    >
      Save
    </Button>
  );

  return (
    <div>
      <EmployeeSalaryTable
        employeeData={data?.data}
        openEmployeeModal={(record, isEditable) => {
          setModalState({
            open: true,
            canEdit: isEditable,
            current: record,
            isDeleteModalVisible: false,
          });
        }}
        deleteRecord={(record) => {
          setModalState({
            open: false,
            canEdit: false,
            current: record,
            isDeleteModalVisible: true,
          });
        }}
        isError={isError}
        isLoading={isLoading}
        setDate={setDate}
        date={date}
      />
      <DeleteModal
        onCancel={() =>
          setModalState((prevState) => ({
            ...prevState,
            isDeleteModalVisible: false,
          }))
        }
        onOk={handleDelete}
        visible={modalState.isDeleteModalVisible}
        title="Are you sure?"
        message="Do you really want to delete this item? This action cannot be undone."
        isLoading={deleteLoad}
        width={500}
        confirmButtonText="Yes, delete it"
      />
      <Modal
        open={modalState.open}
        onCancel={() => {
          setModalState((prevState) => ({ ...prevState, open: false }));
          resetForm();
        }}
        footer={footerContent}
        width={1000}
        centered
      >
        <SalaryForm
          form={form}
          data={modalState.current}
          selectedDate={date}
          isEdit={modalState.canEdit}
        />
      </Modal>
    </div>
  );
};

export default Salary;
